
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useAddSignal } from "@/hooks/useAddSignal";
import { Mail, BookOpen, Search, Eye, Circle, Sparkles } from "lucide-react";
import Header from "@/components/Header";
import SignalInFocus from "@/components/SignalInFocus";
import AddBookModal from "@/components/AddBookModal";
import ImportLibraryModal from "@/components/ImportLibraryModal";
import FirstLightCard from "@/components/FirstLightCard";
import TransmissionsList from "@/components/TransmissionsList";
import { ReadingInsightsModal } from "@/components/ReadingInsightsModal";
import { StandardButton } from "@/components/ui/standard-button";
import { useEnhancedToast } from "@/hooks/use-enhanced-toast";
import { useAuth } from "@/hooks/useAuth";
import { getTransmissions, saveTransmission, updateTransmission, deleteTransmission, Transmission } from "@/services/transmissionsService";
import { useGSAPAnimations } from "@/hooks/useGSAPAnimations";
import ContributionButton from "@/components/ContributionButton";
import ContactModal from "@/components/ContactModal";
import { searchAppleBooks } from "@/services/appleBooks";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { AuthorPopup } from "@/components/AuthorPopup";
import { resolveAuthorForPopup, ScifiAuthor } from "@/services/scifiAuthorsService";
import { usePatternRecognition } from "@/hooks/usePatternRecognition";
import { SEOHead } from "@/components/SEOHead";
import { supabase } from "@/integrations/supabase/client";
import { STARTER_TRANSMISSIONS, isStarterTransmission } from "@/constants/starterTransmissions";
import { isSameBook } from "@/utils/bookIdentity";
import SandRipple from "@/components/SandRipple";

interface AIRecommendation {
  reason: string;
  cluster_connection: string;
}

const Index = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [books, setBooks] = useState<Transmission[]>([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [editingBook, setEditingBook] = useState<Transmission | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showContactModal, setShowContactModal] = useState(false);
  const [selectedAuthor, setSelectedAuthor] = useState<ScifiAuthor | null>(null);
  const [authorPopupVisible, setAuthorPopupVisible] = useState(false);
  const [showInsightsModal, setShowInsightsModal] = useState(false);
  const [aiRecommendations, setAiRecommendations] = useState<Map<string, AIRecommendation>>(() => new Map());
  /**
   * The book someone arrived here to see, from `?add=` in the weekly email.
   *
   * Held in state rather than read from the URL at render time, because the URL
   * is cleaned immediately — otherwise a refresh would re-trigger the add.
   */
  const [arrivedFor, setArrivedFor] = useState<{ title: string; author: string } | null>(null);

  // Memoize static data to prevent unnecessary re-renders
  const currentSignal = useMemo(() => ({
    title: "Signal Detected",
    author: "Awaiting Transmission"
  }), []);

  const { toast } = useEnhancedToast();
  const { user, loading: authLoading } = useAuth();
  const { mainContainerRef, heroTitleRef, addFeatureBlockRef } = useGSAPAnimations();
  const navigate = useNavigate();
  
  const isAnonymous = !user;

  /**
   * The list, with the book you came here for at the top.
   *
   * Clicking "Childhood's End" in the weekly email dropped you on this page
   * with no sign of Childhood's End anywhere: the handler below required a
   * signed-in user and did nothing at all otherwise, and even when signed in it
   * only raised a toast — the comment beside it claimed to "scroll to existing
   * book" and no such code existed. Since transmissions are ordered
   * newest-first, a book added years ago stayed wherever it was, hundreds of
   * rows down.
   *
   * Signed out matters as much as signed in, and it is not the edge case it
   * looks like: half the newsletter's subscribers have no account, and Paul's
   * own phone was signed out when he opened it. So an anonymous visitor gets
   * the same promise — the title they clicked is the first card — by having it
   * prepended to the starter suggestions when it is not already among them.
   */
  const displayBooks = useMemo(() => {
    const base = user ? books : STARTER_TRANSMISSIONS;
    if (!arrivedFor) return base;

    const i = base.findIndex((b) => isSameBook(b, arrivedFor));
    if (i === 0) return base;
    if (i > 0) return [base[i], ...base.slice(0, i), ...base.slice(i + 1)];

    // Not in the list. For a signed-in reader the add modal is already open, so
    // it will arrive on the next load; showing a card for a book that is not in
    // their library would be a lie. For an anonymous visitor the list is only
    // suggestions, so leading with the one they actually asked for is honest
    // and is the whole point of the link.
    if (user) return base;
    return [
      {
        id: -999,
        title: arrivedFor.title,
        author: arrivedFor.author,
        status: 'want-to-read',
        tags: [],
        notes: '',
        cover_url: '',
        rating: {},
        user_id: '',
        created_at: new Date().toISOString(),
      } as Transmission,
      ...base,
    ];
  }, [user, books, arrivedFor]);

  // Pattern recognition for subtle intelligence
  const { getBookBridges } = usePatternRecognition(displayBooks);

  const loadTransmissions = useCallback(async () => {
    if (!user) {
      console.log('No user found, skipping transmission load');
      setLoading(false);
      setError(null);
      return;
    }
    
    try {
      console.log('Loading transmissions for user:', user.email);
      setLoading(true);
      setError(null);
      
      const transmissions = await getTransmissions();
      console.log('Loaded transmissions:', transmissions.length);
      setBooks(transmissions);
    } catch (error: any) {
      console.error('Failed to load transmissions:', error);
      setError('Failed to load transmissions. Please check your connection and try again.');
      
      // Don't show toast if user is not authenticated
      if (user) {
        toast({
          title: "Connection Issue",
          description: "Unable to load your transmissions. Please refresh the page.",
          variant: "destructive",
        });
      }
    } finally {
      setLoading(false);
    }
  }, [toast, user]);

  // "Add Signal" links (newsletter, shared links) add the book directly.
  const onLinkAdded = useCallback(() => { void loadTransmissions(); }, [loadTransmissions]);
  const addSignal = useAddSignal({ onAdded: onLinkAdded });
  // One add per link: the effect below can run again before the cleaned URL
  // lands, and two overlapping saves would both pass the duplicate check.
  const linkAddedRef = useRef<string | null>(null);
  const addFromLink = useCallback((book: { title: string; author: string }) => {
    const key = `${book.title}|${book.author}`;
    if (linkAddedRef.current === key) return;
    linkAddedRef.current = key;
    return addSignal(book);
  }, [addSignal]);

  useEffect(() => {
    console.log('Auth state changed - User:', user?.email, 'Auth loading:', authLoading);
    
    // Only proceed if auth is not loading
    if (!authLoading) {
      if (user) {
        loadTransmissions();
      } else {
        console.log('No authenticated user, setting loading to false');
        setLoading(false);
        setError(null);
      }
    }
  }, [user, authLoading, loadTransmissions]);

  /**
   * `?add=` from the weekly email.
   *
   * Runs for EVERYONE, not just signed-in users. It used to be gated on `user`,
   * so an anonymous visitor — half the subscriber list — got no toast, no
   * modal, not even the URL tidied: the link simply did nothing and dropped
   * them on a page of starter suggestions.
   *
   * Waits for auth to settle. Firing while `authLoading` is true would read a
   * signed-in reader as anonymous and offer to add a book they already own.
   */
  useEffect(() => {
    if (authLoading) return;
    /**
     * And wait for the LIBRARY, not just for auth. `books` starts empty and
     * fills asynchronously, so deciding "is it already in there?" on the first
     * pass answers no for everyone — the URL is cleaned straight afterwards, so
     * the effect never gets a second chance to notice. That would offer to add
     * a duplicate of a book already on the shelf.
     */
    if (user && loading) return;

    const addTitle = searchParams.get('add');
    if (!addTitle) return;
    const addAuthor = searchParams.get('author') || '';

    // Remembered so displayBooks can lead with it, and cleaned from the URL so
    // a refresh does not re-open the add modal.
    setArrivedFor({ title: addTitle, author: addAuthor });

    if (user) {
      /**
       * isSameBook, not `===` on both fields. The old check demanded the
       * author string match exactly, and the catalogue spells authors
       * freehand — "Edwin Abbott" against "Edwin A. Abbott" missed, so a book
       * already on the shelf was offered as a new one.
       */
      const existing = books.find((b) => isSameBook(b, { title: addTitle, author: addAuthor }));
      if (existing) {
        toast({
          title: 'Already in your transmissions',
          description: `"${existing.title}" is at the top of your library.`,
        });
      } else {
        // Added straight away as "want to read"; the reader can edit it from
        // the shelf afterwards. It used to open the edit form first, labelled
        // "Edit Signal / Update Signal" for a book that was not on the shelf.
        void addFromLink({ title: addTitle, author: addAuthor });
      }
    }

    const newParams = new URLSearchParams(searchParams);
    newParams.delete('add');
    newParams.delete('author');
    setSearchParams(newParams, { replace: true });
  }, [searchParams, user, authLoading, loading, books, setSearchParams, toast, addFromLink]);

  // Load AI recommendations for transmissions (deferred for performance, auth only)
  useEffect(() => {
    if (!user || books.length < 5) return;
    const loadAIRecommendations = async () => {
      
      try {
        const { data, error } = await supabase.functions.invoke('ai-book-recommendations', {
          body: { 
            userTransmissions: books.map(t => ({
              title: t.title,
              author: t.author,
              tags: t.tags.join(', '),
              publication_year: t.publication_year
            })),
            limit: 10
          }
        });
        
        if (!error && data?.recommendations) {
          const newAiRecs: Map<string, AIRecommendation> = new Map();
          for (const rec of data.recommendations) {
            // Store by lowercase title for matching
            newAiRecs.set(rec.title.toLowerCase().trim(), rec);
          }
          setAiRecommendations(newAiRecs);
        }
      } catch (aiError) {
        console.error('AI recommendations for transmissions failed:', aiError);
      }
    };
    
    // Delay to ensure fast initial render
    const timer = setTimeout(loadAIRecommendations, 800);
    return () => clearTimeout(timer);
  }, [books]);

  const addBook = useCallback(async (newBook: any) => {
    try {
      setError(null);

      // Enrich with Apple Books link before saving (best-effort)
      const enriched = { ...newBook };
      try {
        const apple = await searchAppleBooks(newBook.title, newBook.author, newBook.isbn);
        if (apple?.storeUrl) {
          enriched.apple_link = apple.storeUrl;
        }
      } catch (e) {
        console.warn('Apple enrichment failed:', e);
      }

      if (editingBook) {
        await updateTransmission(editingBook.id, enriched);
        toast({
          title: "Signal Updated",
          description: "Your transmission has been successfully modified.",
          variant: "success"
        });
      } else {
        await saveTransmission(enriched);
        toast({
          title: "Signal Logged",
          description: "New transmission added to your consciousness record.",
          variant: "success"
        });
      }
      setEditingBook(null);
      // Outside the save's error handling: a reload hiccup after a successful
      // save used to be reported as "Failed to save transmission".
      void loadTransmissions();
    } catch (error: any) {
      console.error('Error saving transmission:', error);
      const message = (error?.message || '').toLowerCase();
      if (message.startsWith('duplicate:')) {
        // saveTransmission's own duplicate guard. Every other add path says
        // this kindly; this one showed the red "Failed to save" instead.
        toast({
          title: "Already in your transmissions",
          description: `"${newBook.title}" is already on your shelf.`,
        });
      } else if (message.includes('user not authenticated')) {
        toast({
          title: "Sign in required",
          description: "Please sign in to log signals.",
          variant: "destructive",
        });
        navigate('/auth');
      } else {
        toast({
          title: "Transmission Error",
          description: "Failed to save transmission. Please try again.",
          variant: "destructive",
        });
      }
    }
  }, [editingBook, toast, loadTransmissions]);

  const handleEditBook = useCallback((book: Transmission) => {
    if (isStarterTransmission(book)) {
      toast({ title: "Sign up to manage your library", description: "Create a free account to add and edit books.", variant: "default" });
      navigate('/auth');
      return;
    }
    setEditingBook(book);
    setIsAddModalOpen(true);
  }, [toast, navigate]);

  const handleKeepBook = useCallback(async (book: Transmission) => {
    if (isStarterTransmission(book)) {
      toast({ title: "Sign up to save favourites", description: "Create a free account to keep books.", variant: "default" });
      navigate('/auth');
      return;
    }
    try {
      const newFavoriteStatus = !book.is_favorite;
      
      await updateTransmission(book.id, { 
        is_favorite: newFavoriteStatus 
      });
      
      await loadTransmissions();
      
      toast({
        title: newFavoriteStatus ? "Signal Kept" : "Keep Status Removed",
        description: newFavoriteStatus 
          ? `"${book.title}" has been marked for retention.`
          : `"${book.title}" is no longer marked as kept.`,
        variant: "success"
      });
    } catch (error) {
      console.error('Error updating favorite status:', error);
      toast({
        title: "Error",
        description: "Failed to update book status. Please try again.",
        variant: "destructive",
      });
    }
  }, [toast, loadTransmissions]);

  const handleDiscardBook = useCallback(async (book: Transmission) => {
    if (isStarterTransmission(book)) {
      toast({ title: "Sign up to manage your library", description: "Create a free account to curate your collection.", variant: "default" });
      navigate('/auth');
      return;
    }
    try {
      setError(null);
      await deleteTransmission(book.id);
      await loadTransmissions();
      toast({
        title: "Signal Discarded",
        description: `"${book.title}" has been removed from your transmissions.`,
        variant: "success"
      });
    } catch (error: any) {
      console.error('Error deleting transmission:', error);
      toast({
        title: "Error",
        description: "Failed to delete transmission. Please try again.",
        variant: "destructive",
      });
    }
  }, [toast, loadTransmissions]);

  const closeModal = useCallback(() => {
    setIsAddModalOpen(false);
    setEditingBook(null);
  }, []);

  const handleRetry = useCallback(() => {
    setError(null);
    loadTransmissions();
  }, [loadTransmissions]);

  const handleAuthorClick = async (authorName: string) => {
    console.log('Author clicked:', authorName);
    try {
      const authorData = await resolveAuthorForPopup(authorName);
      
      if (authorData) {
        setSelectedAuthor(authorData);
        setAuthorPopupVisible(true);
      } else {
        const fallbackAuthor: ScifiAuthor = {
          id: crypto.randomUUID(),
          name: authorName,
          bio: undefined,
          notable_works: [],
          needs_enrichment: true,
          data_quality_score: 0,
          birth_year: undefined,
          death_year: undefined,
          last_enriched: undefined,
          enrichment_attempts: 0,
          nationality: undefined,
          data_source: 'manual',
          verification_status: 'pending'
        };
        setSelectedAuthor(fallbackAuthor);
        setAuthorPopupVisible(true);
      }
    } catch (error) {
      console.error('Error fetching author:', error);
    }
  };

  const closeAuthorPopup = () => {
    setAuthorPopupVisible(false);
    setSelectedAuthor(null);
  };


  return (
    <>
      <SEOHead
        title="Library"
        description="Your personal sci-fi book collection with transmissions, patterns, and neural connections"
        keywords={['sci-fi library', 'book tracker', 'reading list', 'science fiction books']}
      />
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 overflow-x-hidden">
        <Header />
        
        <main 
          id="main-content"
          ref={mainContainerRef} 
          className="container mx-auto section-padding"
          role="main"
          aria-label="Your book transmissions"
        >
          <div ref={addFeatureBlockRef} className="feature-block">
            <SignalInFocus 
              book={currentSignal} 
              onClick={() => setShowInsightsModal(true)}
            />
          </div>
          
          {/* Under the Signal in Focus card and above the list: something moving
              beneath a page of signals, where it can actually be seen. */}
          <div className="mb-6">
            <SandRipple />
          </div>

          <div ref={addFeatureBlockRef} className="feature-block flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
            <div className="flex-1">
              <h2 ref={heroTitleRef} className="text-slate-200 text-xl font-medium mb-1">Transmissions</h2>
              <p className="text-slate-400 text-sm">
                Your reading frequency across time and space
                <span className="ml-2 inline-flex items-center gap-1.5 text-xs">
                  <span aria-hidden="true">•</span>
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
                  <span>Signal archive: Active</span>
                </span>
              </p>
            </div>
            
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  if (isAnonymous) {
                    toast({ title: "Sign up to import", description: "Create a free account to bring your library across.", variant: "default" });
                    navigate('/auth');
                    return;
                  }
                  setIsImportOpen(true);
                }}
                className="h-9 px-3 rounded-lg border border-slate-700/60 text-slate-300 hover:text-cyan-300 hover:border-cyan-400/40 text-sm transition-colors whitespace-nowrap active:scale-95 touch-manipulation"
                aria-label="Import your library from Goodreads or StoryGraph"
              >
                Import
              </button>
              <StandardButton
                onClick={() => {
                  if (isAnonymous) {
                    toast({ title: "Sign up to log signals", description: "Create a free account to build your library.", variant: "default" });
                    navigate('/auth');
                    return;
                  }
                  setIsAddModalOpen(true);
                }}
                variant="standard"
                /* h-9 + text-sm so this sits at exactly the same height as the
                   Import button beside it — StandardButton's default sizing is
                   padding-based, which left the pair visibly mismatched. */
                className="h-9 text-sm touch-manipulation active:scale-95 whitespace-nowrap"
                aria-label="Log a new book to your library"
              >
                + Log Signal
              </StandardButton>
            </div>
          </div>

          {/* Sign-up nudge banner for anonymous visitors */}
          {isAnonymous && (
            <div className="mb-4 px-4 py-3 rounded-lg border border-cyan-500/20 bg-cyan-500/5">
              <p className="text-slate-300 text-sm">
                <span className="text-cyan-400 font-medium">Curated for you</span> — these are starter suggestions to help you explore.{' '}
                <Link to="/auth" className="text-blue-400 hover:text-blue-300 underline underline-offset-2 font-medium">
                  Sign up free
                </Link>{' '}
                to build your own library.
              </p>
            </div>
          )}
          
          <div ref={addFeatureBlockRef} className="feature-block">
            {error ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full border-2 border-dashed border-red-600 flex items-center justify-center">
                  <div className="w-6 h-6 rounded-full bg-red-500" />
                </div>
                <h3 className="text-red-400 text-lg mb-2">Signal Error</h3>
                <p className="text-slate-400 mb-4">{error}</p>
                <StandardButton onClick={handleRetry} variant="standard">
                  Try Again
                </StandardButton>
              </div>
            ) : user && !loading && books.length === 0 ? (
              <FirstLightCard
                onImport={() => setIsImportOpen(true)}
                onLogBook={() => setIsAddModalOpen(true)}
              />
            ) : (
              <TransmissionsList
                transmissions={displayBooks}
                loading={isAnonymous ? false : loading}
                onEdit={handleEditBook}
                onKeep={handleKeepBook}
                onDiscard={handleDiscardBook}
                onAddNew={() => {
                  if (isAnonymous) {
                    navigate('/auth');
                    return;
                  }
                  setIsAddModalOpen(true);
                }}
                onAuthorClick={handleAuthorClick}
                getBookBridges={getBookBridges}
                aiRecommendations={aiRecommendations}
              />
            )}
          </div>

          <div className="text-center mt-16">
            <span className="inline-flex items-center space-x-2 text-slate-400 text-xs">
              <span className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse inline-block" />
              <span>Transmission relay: Active</span>
            </span>
          </div>

          
        </main>
        
        <AddBookModal
          isOpen={isAddModalOpen}
          onClose={closeModal}
          onAdd={addBook}
          editingBook={editingBook || undefined}
        />

        <ImportLibraryModal
          isOpen={isImportOpen}
          onClose={() => setIsImportOpen(false)}
          onImported={loadTransmissions}
        />

        <ContactModal 
          isOpen={showContactModal} 
          onClose={() => setShowContactModal(false)} 
        />

        <AuthorPopup
          author={selectedAuthor}
          isVisible={authorPopupVisible}
          onClose={closeAuthorPopup}
          onAuthorUpdate={(updatedAuthor) => {
            setSelectedAuthor(updatedAuthor);
          }}
        />

        <ReadingInsightsModal
          isOpen={showInsightsModal}
          onClose={() => setShowInsightsModal(false)}
          transmissions={books}
        />
      </div>
    </>
  );
};

export default Index;
